import { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardActions from "@mui/material/CardActions";
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { useNavigate } from "react-router-dom";

const Project = ({ Project, reverse = false }) => {
  const [ImgSrc, setImgSrc] = useState("");
  const [isHovered, setIsHovered] = useState(false);
  const storage = getStorage();
  const navigate = useNavigate();

  useEffect(() => {
    const getImage = async () => {
      try {
        const imageRef = ref(storage, `PastProjects/${Project.id}/CoverImage`);
        const image = await getDownloadURL(imageRef);
        setImgSrc(image);
      } catch (err) {
        console.warn("Image not found for project:", Project.id);
      }
    };
    getImage();
  }, [Project.id, storage]);

  return (
    <Card
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        background: "rgba(255, 255, 255, 0.03)",
        backdropFilter: "blur(10px)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
        borderRadius: "1rem",
        overflow: "hidden",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        boxShadow: isHovered
          ? "0 20px 40px -5px rgba(0, 0, 0, 0.4)"
          : "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
        transform: isHovered ? "translateY(-8px)" : "translateY(0)",
      }}
    >
      <Grid
        container
        direction={reverse ? "row-reverse" : "row"}
        alignItems="stretch"
      >
        <Grid item xs={12} md={7}>
          <MKBox
            sx={{
              height: "100%",
              width: "100%",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <MKBox
              component="img"
              src={ImgSrc}
              alt={Project.title}
              sx={{
                width: "100%",
                height: { xs: 260, md: "100%" }, // Fill height on desktop
                minHeight: { md: 400 },
                objectFit: "cover",
                transition: "transform 0.6s ease",
                transform: isHovered ? "scale(1.05)" : "scale(1)",
              }}
            />
            {/* Gradient Overlay on Image */}
            <MKBox
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background:
                  "linear-gradient(to right, rgba(15, 23, 42, 0.1), rgba(15, 23, 42, 0) 50%, rgba(15, 23, 42, 0.1))",
                pointerEvents: "none",
              }}
            />
          </MKBox>
        </Grid>

        {/* Right Content */}
        <Grid
          item
          xs={12}
          md={5}
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: { xs: 3, md: 4 },
            py: { xs: 3, md: 5 },
          }}
        >
          <CardContent sx={{ p: 0, mb: 3 }}>
            <MKTypography
              variant="h3"
              fontWeight="bold"
              color="white"
              gutterBottom
              sx={{
                letterSpacing: "-0.5px",
                background: "linear-gradient(to right, #fff, #94a3b8)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {Project.title}
            </MKTypography>

            <MKTypography
              variant="body1"
              color="white"
              sx={{
                lineHeight: 1.7,
                opacity: 0.8,
                fontWeight: 300,
              }}
            >
              {Project.description}
            </MKTypography>
          </CardContent>

          <CardActions sx={{ p: 0, display: "flex", gap: 2 }}>
            <MKButton
              variant="contained"
              color="info" // Using theme color if available
              onClick={() =>
                navigate("/PastProjects/Details", {
                  state: { project: Project },
                })
              }
              sx={{
                textTransform: "none",
                fontSize: "0.95rem",
                fontWeight: 600,
                px: 3,
                py: 1.2,
                borderRadius: "0.5rem",
                background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 16px rgba(37, 99, 235, 0.4)",
                  background:
                    "linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)",
                },
              }}
            >
              Learn More
            </MKButton>

            {Project.link && (
              <MKButton
                component="a"
                href={Project.link}
                target="_blank"
                rel="noopener noreferrer"
                variant="outlined"
                color="white"
                sx={{
                  textTransform: "none",
                  fontSize: "0.95rem",
                  fontWeight: 600,
                  px: 3,
                  py: 1.2,
                  borderRadius: "0.5rem",
                  borderColor: "rgba(255,255,255,0.3)",
                  "&:hover": {
                    borderColor: "white",
                    backgroundColor: "rgba(255,255,255,0.05)",
                  },
                }}
              >
                Visit Live
              </MKButton>
            )}
          </CardActions>
        </Grid>
      </Grid>
    </Card>
  );
};

export default Project;
